import { type ReactNode } from "react"
import { parseLocalDate, formatLocalDate } from "@/lib/date"

const TAILWIND_HEX: Record<string, string> = {
  gray: "#6b7280", slate: "#64748b", zinc: "#71717a", neutral: "#737373", stone: "#78716c",
  red: "#ef4444", orange: "#f97316", amber: "#f59e0b", yellow: "#eab308", lime: "#84cc16",
  green: "#22c55e", emerald: "#10b981", teal: "#14b8a6", cyan: "#06b6d4", sky: "#0ea5e9",
  blue: "#3b82f6", indigo: "#6366f1", violet: "#8b5cf6", purple: "#a855f7",
  fuchsia: "#d946ef", pink: "#ec4899", rose: "#f43f5e",
}

export function resolveColor(color: string): string {
  return TAILWIND_HEX[color] ?? color
}

import type { DataTableColumnDef } from "@/components/data-table"
import { DataTableCell } from "@/components/data-table"
import { Switch } from "@/components/ui/switch"
import { PersonCombobox } from "@/components/ui/person-combobox"
import { DatePicker } from "@/components/ui/date-range-picker"
import {
  CellSelect,
  CellTextField,
  type CellSelectOption,
} from "@/components/cell-controls/cell-controls"
import { useProjects } from "@/api/projects/useProjects"
import type { AttributeDefinition } from "@/api/metadata/useAttributeDefinitions"
import { getAttributeIcon } from "@/features/attributes/attribute-presentation"
import { PeopleMultiSelect } from "@/features/attributes/people-multi-select"

type ColumnRow = {
  id: string
}

type BuildAttributeColumnsOptions<TRow extends ColumnRow> = {
  definitions: AttributeDefinition[]
  getValue: (row: TRow, definition: AttributeDefinition) => unknown
  onUpdate: (row: TRow, definition: AttributeDefinition, value: unknown) => void
  includeSlugs?: string[]
  excludeSlugs?: string[]
  sizeBySlug?: Record<string, number>
  defaultVisibleSlugs?: string[]
  renderCellBySlug?: Partial<
    Record<
      string,
      (args: {
        row: TRow
        definition: AttributeDefinition
        value: unknown
        onUpdate: (row: TRow, definition: AttributeDefinition, value: unknown) => void
      }) => ReactNode
    >
  >
}

function ProjectReferenceCell({
  value,
  onCommit,
}: {
  value: string | null
  onCommit: (value: string | null) => void
}) {
  const { projects } = useProjects()
  const options: CellSelectOption[] = projects.map((project) => ({
    value: project.id,
    label: project.name,
  }))

  return (
    <DataTableCell>
      <CellSelect
        value={value}
        options={options}
        onChange={onCommit}
        clearable
        searchPlaceholder="Поиск проекта..."
        itemVariant="plain"
      />
    </DataTableCell>
  )
}

function AttributeValueCell<TRow extends ColumnRow>({
  row,
  definition,
  value,
  onCommit,
}: {
  row: TRow
  definition: AttributeDefinition
  value: unknown
  onCommit: (row: TRow, definition: AttributeDefinition, value: unknown) => void
}) {
  const isPersonReference =
    definition.attribute_type === "user" ||
    (definition.attribute_type === "reference" &&
      definition.config.target_object_type === "person")

  if (isPersonReference) {
    return (
      <DataTableCell>
        <PersonCombobox
          value={typeof value === "string" ? value : null}
          onChange={(personId) => onCommit(row, definition, personId || null)}
        />
      </DataTableCell>
    )
  }

  const isProjectReference =
    definition.attribute_type === "reference" &&
    definition.config.target_object_type === "project"

  if (isProjectReference) {
    return (
      <ProjectReferenceCell
        value={typeof value === "string" ? value : null}
        onCommit={(nextValue) => onCommit(row, definition, nextValue)}
      />
    )
  }

  const isPersonReferenceList =
    definition.attribute_type === "reference_list" &&
    definition.config.target_object_type === "person"

  if (isPersonReferenceList) {
    const ids = Array.isArray(value)
      ? value.filter((id): id is string => typeof id === "string" && id.length > 0)
      : null
    return (
      <DataTableCell>
        <PeopleMultiSelect
          value={ids}
          onCommit={(next) => onCommit(row, definition, next)}
        />
      </DataTableCell>
    )
  }

  switch (definition.attribute_type) {
    case "checkbox":
      return (
        <DataTableCell className="px-3">
          <Switch
            checked={value === true}
            onCheckedChange={(nextValue) => onCommit(row, definition, nextValue)}
          />
        </DataTableCell>
      )
    case "select": {
      const options: CellSelectOption[] = (definition.config.options ?? []).map((opt) => ({
        value: opt.value,
        label: opt.label,
        color: opt.color ? resolveColor(opt.color) : undefined,
      }))
      return (
        <DataTableCell>
          <CellSelect
            value={typeof value === "string" ? value : null}
            options={options}
            onChange={(next) => onCommit(row, definition, next)}
            renderValue={(opt) =>
              opt?.color ? (
                <span className="flex items-center gap-1.5">
                  <span
                    className="size-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: opt.color }}
                  />
                  <span className="truncate">{opt.label}</span>
                </span>
              ) : (
                opt?.label
              )
            }
            itemVariant="plain"
          />
        </DataTableCell>
      )
    }
    case "date": {
      const dateValue =
        typeof value === "string" && value && !Number.isNaN(Date.parse(value))
          ? parseLocalDate(value)
          : undefined
      return (
        <DataTableCell>
          <DatePicker
            mode="single"
            value={dateValue}
            onChange={(date) =>
              onCommit(
                row,
                definition,
                date ? formatLocalDate(date) : null,
              )
            }
          />
        </DataTableCell>
      )
    }
    case "datetime":
      return (
        <DataTableCell>
          <CellTextField
            type="datetime-local"
            value={typeof value === "string" ? value : null}
            onCommit={(next) => onCommit(row, definition, next)}
          />
        </DataTableCell>
      )
    case "number":
      return (
        <DataTableCell>
          <CellTextField
            type="number"
            value={
              typeof value === "number"
                ? String(value)
                : typeof value === "string" && value !== ""
                  ? value
                  : null
            }
            onCommit={(next) => {
              if (next === null) {
                onCommit(row, definition, null)
                return
              }
              const parsed = Number(next)
              onCommit(row, definition, Number.isNaN(parsed) ? null : parsed)
            }}
            parse={(raw) => (raw.trim() === "" ? null : raw)}
            format={(v) => v ?? ""}
          />
        </DataTableCell>
      )
    default:
      return (
        <DataTableCell>
          <CellTextField
            value={typeof value === "string" || typeof value === "number" ? String(value) : null}
            onCommit={(next) => onCommit(row, definition, next)}
          />
        </DataTableCell>
      )
  }
}

export function buildAttributeColumns<TRow extends ColumnRow>({
  definitions,
  getValue,
  onUpdate,
  includeSlugs,
  excludeSlugs,
  sizeBySlug,
  defaultVisibleSlugs,
  renderCellBySlug,
}: BuildAttributeColumnsOptions<TRow>): DataTableColumnDef<TRow>[] {
  const includeSet = includeSlugs ? new Set(includeSlugs) : null
  const excludeSet = new Set(excludeSlugs ?? [])
  const defaultVisibleSet = new Set(defaultVisibleSlugs ?? [])

  const selectedDefinitions = definitions
    .filter((definition) => (includeSet ? includeSet.has(definition.slug) : true))
    .filter((definition) => !excludeSet.has(definition.slug))
    .sort((left, right) => {
      if (includeSlugs) {
        return includeSlugs.indexOf(left.slug) - includeSlugs.indexOf(right.slug)
      }
      return left.display_order - right.display_order || left.name.localeCompare(right.name, "ru")
    })

  return selectedDefinitions.map((definition) => {
    const defaultVisible = definition.is_system
      ? defaultVisibleSet.has(definition.slug)
      : true

    return {
      id: `attr:${definition.slug}`,
      meta: {
        label: definition.name,
        defaultVisible,
        icon: getAttributeIcon(
          definition.attribute_type,
          definition.config?.target_object_type,
        ),
      },
      size: sizeBySlug?.[definition.slug] ?? 180,
      accessorFn: (row) => getValue(row, definition),
      cell: ({ row }) => {
        const value = getValue(row.original, definition)
        const override = renderCellBySlug?.[definition.slug]
        if (override) {
          return override({
            row: row.original,
            definition,
            value,
            onUpdate,
          })
        }

        return (
          <AttributeValueCell
            row={row.original}
            definition={definition}
            value={value}
            onCommit={onUpdate}
          />
        )
      },
    } satisfies DataTableColumnDef<TRow>
  })
}